/**
 * AMBIENT FORM COLUMNS
 */

import { ColumnDefinition, COLUMN_PRESETS } from '../../../universal_table';
import weatherOptionsData from '../cell_data/weatherOptions.json';

export const AMBIENT_COLUMNS: ColumnDefinition[] = [
  {
    key: '_delete',
    label: '',
    type: 'action',
    sticky: true,
    minWidth: 40,
    editable: false
  },
  {
    key: 'entry_date',
    label: 'Date',
    ...COLUMN_PRESETS.DATE,
    sticky: true,
    minWidth: 110
  },
  {
    key: 'sunrise_time',
    label: 'Sunrise Time (POA≥2.3 W/m²)',
    abbreviation: 'Sunrise',
    ...COLUMN_PRESETS.TIME,
    minWidth: 120
  },
  {
    key: 'sunset_time',
    label: 'Sunset Time (POA<3 W/m²)',
    abbreviation: 'Sunset',
    ...COLUMN_PRESETS.TIME,
    minWidth: 120
  },
  {
    key: 'uptime_minutes',
    label: 'Uptime Minutes',
    abbreviation: 'Operation Time ',
    unitLabel: 'Minutes',
    ...COLUMN_PRESETS.CALCULATED,
    suffix: '',
    minWidth: 120,
    calculateFn: (rowData) => {
      if (!rowData.sunrise_time || !rowData.sunset_time) return null;
      
      // Helper function to parse time string (handles both 24-hour and 12-hour with AM/PM)
      const parseTimeToMinutes = (timeStr: string): number | null => {
        if (!timeStr || typeof timeStr !== 'string' || timeStr.trim() === '') return null;
        try {
          // Remove any whitespace
          const cleanTime = timeStr.trim().toUpperCase();
          
          // Check if it has AM/PM
          const hasAMPM = cleanTime.includes('AM') || cleanTime.includes('PM');
          
          if (hasAMPM) {
            // Handle 12-hour format with AM/PM
            const isPM = cleanTime.includes('PM');
            const timePart = cleanTime.replace(/AM|PM/gi, '').trim();
            const [hours, minutes = '0'] = timePart.split(':');
            let h = parseInt(hours, 10);
            const m = parseInt(minutes, 10);
            
            if (isNaN(h) || isNaN(m)) return null;
            
            // Convert to 24-hour format
            if (isPM && h !== 12) h += 12;
            if (!isPM && h === 12) h = 0;
            
            return h * 60 + m;
          } else {
            // Handle 24-hour format
            const [hours, minutes = '0'] = cleanTime.split(':');
            const h = parseInt(hours, 10);
            const m = parseInt(minutes, 10);
            
            if (isNaN(h) || isNaN(m)) return null;
            
            return h * 60 + m;
          }
        } catch {
          return null;
        }
      };
      
      try {
        const sunriseStr = rowData.sunrise_time.toString().trim();
        const sunsetStr = rowData.sunset_time.toString().trim();
        
        const sunriseMinutes = parseTimeToMinutes(sunriseStr);
        const sunsetMinutes = parseTimeToMinutes(sunsetStr);
        
        if (sunriseMinutes === null || sunsetMinutes === null) return null;
        
        let calculated = sunsetMinutes - sunriseMinutes;
        
        // Handle case where sunset is next day (sunset < sunrise)
        if (calculated < 0) {
          // Assume sunset is next day, add 24 hours (1440 minutes)
          calculated = (1440 - sunriseMinutes) + sunsetMinutes;
        }
        
        return calculated > 0 ? calculated : null;
      } catch {
        return null;
      }
    },
    dependencies: ['sunrise_time', 'sunset_time']
  },
  {
    key: 'ghi_kwh_per_m2',
    label: 'Global Horizontal Irradiance (kWh/m²)',
    abbreviation: 'GHI',
    unitLabel: 'kWh/m²',
    ...COLUMN_PRESETS.NUMBER,
    suffix: 'kWh/m²',
    minWidth: 120,
    decimalPlaces: 1
  },
  {
    key: 'poa_kwh_per_m2',
    label: 'Plane of Array (kWh/m²)',
    abbreviation: 'GII',
    unitLabel: 'kWh/m²',
    ...COLUMN_PRESETS.NUMBER,
    suffix: 'kWh/m²',
    minWidth: 120,
    decimalPlaces: 1
  },
  {
    key: 'avg_ambient_temp_c',
    label: 'Avg Ambient Temp (°C)',
    abbreviation: 'Ambient (Avg)',
    unitLabel: '°C',
    ...COLUMN_PRESETS.NUMBER,
    suffix: '°C',
    minWidth: 120,
    decimalPlaces: 1
  },
  {
    key: 'max_ambient_temp_c',
    label: 'Max Ambient Temp (°C)',
    abbreviation: 'Ambient (Max)',
    unitLabel: ' °C',
    ...COLUMN_PRESETS.NUMBER,
    suffix: '°C',
    minWidth: 120,
    decimalPlaces: 1
  },
  {
    key: 'avg_module_temp_c',
    label: 'Avg Module Temp (°C)',
    abbreviation: 'Module (Avg)',
    unitLabel: ' °C',
    ...COLUMN_PRESETS.NUMBER,
    suffix: '°C',
    minWidth: 120,
    decimalPlaces: 1
  },
  {
    key: 'max_module_temp_c',
    label: 'Max Module Temp (°C)',
    abbreviation: 'Module (Max)',
    unitLabel: '°C',
    ...COLUMN_PRESETS.NUMBER,
    suffix: '°C',
    minWidth: 120,
    decimalPlaces: 1
  },
  {
    key: 'avg_air_speed_ms',
    label: 'Avg Wind Speed (m/s)',
    abbreviation: 'Wind (Avg)',
    ...COLUMN_PRESETS.NUMBER,
    suffix: 'm/s',
    minWidth: 120,
    decimalPlaces: 1
  },
  {
    key: 'max_air_speed_ms',
    label: 'Max Wind Speed (m/s)',
    abbreviation: 'Wind (Max)',
    ...COLUMN_PRESETS.NUMBER,
    suffix: 'm/s',
    minWidth: 120,
    decimalPlaces: 1
  },
  {
    key: 'weather_status',
    label: 'Weather Status',
    abbreviation: 'Weather',
    type: 'select',
    width: 'auto',
    minWidth: 130,
    options: weatherOptionsData.options
  }
];
